import UserProfile from '@zeus/core/UserProfile';
import ExtractorVersionTag from '_/features/extractors/common/ExtractorVersionTag';
import { formatDateTime, formatRelativeTime } from '_/utils/dateUtils';
import { compareAssigneeSort, compareSort } from '_/utils/formatUtils';
import { Table, Typography } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import React, { ReactElement } from 'react';

interface ExtractorVersion {
  id: string;
  addedAt: string;
  editor?: any;
  tag?: string;
  previousTag?: string;
}

interface ExtractorVersionsDrilldownTableProps {
  extractorId: string;
  extractorVersions: ExtractorVersion[];
  isLoading?: boolean;
  loadMoreExtractorVersions: (extractorId: string) => void;
}

function ExtractorVersionsDrilldownTable({
  extractorVersions,
  isLoading = false,
}: ExtractorVersionsDrilldownTableProps): ReactElement {
  const columnDefs: ColumnType<ExtractorVersion>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 250,
      render: (id: string) => <Typography.Text style={{ fontSize: '12px' }}>{id}</Typography.Text>,
      sorter: compareSort('id'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Added at',
      dataIndex: 'addedAt',
      render: (record: string) => (
        <div>
          <div>{formatDateTime(record)}</div>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {formatRelativeTime(record)}
          </Typography.Text>
        </div>
      ),
      width: 400,
      sorter: compareSort('addedAt'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Editor',
      dataIndex: 'lastEditor',
      render: (_: string, record: ExtractorVersion) => <UserProfile tableMode showDetails user={record.editor} />,
      sorter: compareAssigneeSort,
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Git Tag',
      dataIndex: 'tag',
      sorter: compareSort('tag'),
      sortDirections: ['ascend', 'descend'] as const,
      render: (_: string, { tag, previousTag }: ExtractorVersion) => <ExtractorVersionTag current={tag} previous={previousTag} />,
    },
  ];

  return (
    <Table
      virtual
      loading={isLoading}
      columns={columnDefs}
      dataSource={extractorVersions}
      tableLayout={'fixed'}
      indentSize={0}
      rowKey={(record) => record.id}
      scroll={{ y: 500 }}
      pagination={false}
    />
  );
}

export default ExtractorVersionsDrilldownTable;
